import { useFormikContext } from "formik";
import React, { FC, useState } from "react";
import { toast } from "react-toastify";
import { DEF_PG_SIZE_SUB } from "../../constants/maximum-page-size";
import { CC_RECIPIENT, TO_RECIPIENT } from "../../constants/notification-key";
import { useLocale } from "../../contexts/localeContext";
import { TableContextProvider } from "../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { Button } from '../../components/button/Button';
import EnrolmentColumns from "../../models/ColumnDefinitions/EnrolmentColumns";
import { ListData } from "../../models/ListData";
import { ListQuery } from "../../models/ListQuery";
import { Lookup } from "../../models/Lookup";
import LookupTypes from "../../models/LookupTypes";
import CategorySearch from "../../models/search/CategorySearch";
import CompanySearch from "../../models/search/CompanySearch";
import DepartmentSearch from "../../models/search/DepartmentSearch";
import EnrolmentSearch from "../../models/search/EnrolmentSearch";
import SiteSearch from "../../models/search/SiteSearch";
import { SettingData } from "../../models/SettingData";
import { TabSummary } from "../../models/TabSummary";
import { hideColumns, hideLookups } from "../../models/utilities/columnHelper";
import { getCategoryNameWithPagination } from "../../services/categoryService";
import { getCompanyNamesWithPagination } from "../../services/companyService";
import { getDepartmentNamesWithPagination } from "../../services/departmentService";
import { getEnrolmentNamesWithPagination, getEnrolments } from "../../services/enrolmentService";
import { handleManage } from "../../services/notificationService";
import { getSiteNamesWithPagination } from "../../services/siteService";
import { getErrorMessage } from "../../services/utilities/ServiceError";
import { appendFilter } from "../../utilities/filters";
import ActivityFrame from "../activity-frame/activity-frame";
import { useDialog } from "../dialog/DialogContext";
import TableTab from "../tabs/table-tab";
import Tabs from "../tabs/tabs";
import AssignModalTwo from "../assignWizard/AssignModal";
import { PaginationModel } from "@/models/Pagination";

interface RecipientsProps {
    ccRecipients: string[];
    toRecipients: string[];
    reloadPage: () => void;
}

const Recipients: FC<RecipientsProps> = ({ ccRecipients, toRecipients, reloadPage }) => {

    const [selectedTab, setSelectedTab] = useState(0);
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.sendTo), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.copyTo), badge: 0, showInitial: true, isActive: true },
    ];
    const [showManagedModal, setShowManagedModal] = useState(false);
    const [selectedToRecipients, setSelectedToRecipients] = useState<string[]>(toRecipients);
    const [selectedCCRecipients, setSelectedCCRecipients] = useState<string[]>(ccRecipients);
    const [busy, setBusy] = useState(false);
    const { values } = useFormikContext<any>();
    const dialog = useDialog();
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs);
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });
    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    const [lookup] = useState<Lookup[]>([{
        name: LookupTypes.site,
        values: [],
        dataMethod: getSiteNamesWithPagination,
        searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
    },
    {
        name: LookupTypes.company,
        values: [],
        dataMethod: getCompanyNamesWithPagination,
        searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
    },
    {
        name: LookupTypes.department,
        values: [],
        dataMethod: getDepartmentNamesWithPagination,
        searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
    },
    {
        name: LookupTypes.profile_id,
        values: [],
        dataMethod: getCategoryNameWithPagination,
        searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
    }]);
    const columns = hideLookups(hideColumns(EnrolmentColumns, [
        "totalTags",
        "category",
        "uid",
        "gender",
        "title",
        "totalAccessGroups",
        "suffix",
        "enabled",
        "masterTypeName",
        "site",
    ]), ['name']);

    const getReportTitle = (selectedTab: number): string => {
        return selectedTab === 0 ? translate.byKey(TranslationKeys.sendToRecipient) : translate.byKey(TranslationKeys.copyToRecipient);
    }

    const getDataMethod = (selected: number): (listParameters?: ListQuery) => Promise<ListData> => {
        const recipients: string = selected === 0 ? toRecipients.toString() : ccRecipients.toString();
        if (recipients) {
            return (listParameters?: ListQuery) => getEnrolments(translate, {
                ...listParameters,
                filters: appendFilter(
                    recipients,
                    "Recipients",
                    "id",
                    listParameters?.filters ?? [],
                    "=in=")
            })
        }

        return (listParameters?: ListQuery) => {
            return new Promise((resolve, reject) => {
                resolve({ rows: [], pagination: { maxPageSize: 0, pageNumber: 1, totalCount: 0, totalPages: 0 } })
            })
        }
    }

    const onTabSelect = (selected: number) => {
        dataRefresh(getDataMethod(selected));
        setSelectedTab(selected);
    }


    const getAssignedItems = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setBusyLoader: React.Dispatch<React.SetStateAction<boolean>>, setAlwaysSelectedAndDisabled?: React.Dispatch<React.SetStateAction<string[]>>) => {
        try {
            setBusyLoader(true);
            let pageNumber = 1, pagination: PaginationModel, ids: string[] = [], rows: any[] = [];
            const recipients: string = selectedTab === 0 ? toRecipients.toString() : ccRecipients.toString();
            const emptyResponseFunction = (listParameters?: ListQuery) => Promise.resolve({ rows: [], pagination: { maxPageSize: 0, pageNumber: 1, totalCount: 0, totalPages: 0 } });
            
            do {
                const enrolments = recipients.length > 0 ?
                    await getEnrolmentNamesWithPagination({ filters: recipients ? appendFilter(recipients, "Recipients", "id", [], "=in=") : [], pageSize: 100, goToPage: pageNumber }) :
                        await emptyResponseFunction();
                
                ids = ids.concat(enrolments.rows.map(row => row.value))
                rows = rows.concat(enrolments.rows.map(row => ({ id: row.value, name: row.label })));
                pagination = enrolments.pagination;
                pageNumber++;
            } while (pageNumber <= pagination.totalPages);
            
            setSelectedIds(ids);
            setSelectedRows(rows)
            setOriginalSelectedIds(ids);
            setOriginalSelectedRows(rows);
            setAlwaysSelectedAndDisabled && setAlwaysSelectedAndDisabled(selectedTab === 0 ? selectedCCRecipients : selectedToRecipients)
        } catch (error) {
            toast.warn(getErrorMessage(error));
        }
        finally {
            setBusyLoader(false);
        }
    }

    const handleSubmit = async (toBeAdded: string[], toBeRemoved: string[]): Promise<void> => {
        const settingDataRefForToRecipients = values.settingDatasRef.filter(settingData => settingData.elementSysname === "TO_RECIPIENT");
        const settingDataRefForCCRecipients = values.settingDatasRef.filter(settingData => settingData.elementSysname === "CC_RECIPIENT");

        const existingToRecipientIds: string[] = settingDataRefForToRecipients.map(settingData => settingData.sdValstring);
        const resultingToRecipientIds = selectedTab === 0
            ? existingToRecipientIds.filter(id => !toBeRemoved.includes(id)).concat(toBeAdded)
            : existingToRecipientIds;

        if (resultingToRecipientIds.length < 1) {
            toast.error(translate.byKey(TranslationKeys.atLeastOneSendToRecipientNeedsToBeSelected));
            return;
        }

        const selectedRecipientType = selectedTab === 0 ? TO_RECIPIENT : CC_RECIPIENT;
        const selectedSettingDataRef = selectedTab === 0 ? settingDataRefForToRecipients : settingDataRefForCCRecipients;

        const settingDataToAdd: SettingData[] = toBeAdded.map((sdValstring) => ({
            sdValstring,
            eSysnameLookup: selectedRecipientType,
            relationship: [
                {
                    id: values.vruleActionId,
                    type: "ruleAction",
                    relationType: "vruleAction"
                },
                {
                    id: sdValstring,
                    type: "master"
                }
            ]
        }));

        const settingDataToRemove = selectedSettingDataRef
            .filter((settingDataRef) => toBeRemoved.includes(settingDataRef.sdValstring))
            .map((settingDataRef) => settingDataRef.id);

        setBusy(true);
        handleManage(settingDataToAdd, settingDataToRemove)
            .then(async () => {
                if (selectedTab === 0) {
                    setSelectedToRecipients(resultingToRecipientIds);
                } else {
                    const existingCCRecipientIds = settingDataRefForCCRecipients.map(settingData => settingData.sdValstring);
                    const resultingCCRecipientIds = existingCCRecipientIds.filter(id => !toBeRemoved.includes(id)).concat(toBeAdded);
                    setSelectedCCRecipients(resultingCCRecipientIds);
                }

                toast.success(translate.byKey(TranslationKeys.recordUpdated));
                await reloadPage();
            })
            .catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
            })
            .finally(() => {
                setBusy(false);
            });
    }

    return <>
        <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white'>
            <Tabs
                tabs={tabs}
                onSelect={onTabSelect}
                selected={selectedTab}
                add={false}
                onAdd={() => { }}
                isFlat={true}
                className="w-full">
                <div className="w-full flex flex-col">
                    <div className="mb-3 w-full border-b border-gray-400" />
                    <TableContextProvider
                        dataMethod={getDataMethod(selectedTab)}
                        manualDataRefresh={manualDataRefresh}
                        tableDescriptor={columns}
                        searchDescriptor={EnrolmentSearch}
                        useTable
                        usePagination
                        useFilter
                        useSearch
                        useSort
                        maxPageSize={DEF_PG_SIZE_SUB}
                        lookup={lookup}
                        canExportReport={false}>
                        <TableTab
                            title={getReportTitle(selectedTab)}
                            selectRows={false}
                            cyName="recipients">
                            <Button variant="submit" shape="rounded" className="py-1 px-5"
                                onClick={() => setShowManagedModal(true)}>
                                {translate.byKey(TranslationKeys.manage)}
                            </Button>
                        </TableTab>
                    </TableContextProvider>
                </div>
            </Tabs>
            <AssignModalTwo
                isOpen={showManagedModal}
                onClose={() => setShowManagedModal(false)}
                modalTitle={getReportTitle(selectedTab)}
                assigningTitle={translate.byKey(TranslationKeys.recipients)}
                lookup={lookup}
                dataMethod={(listParameters) => getEnrolments(translate, {
                    ...listParameters,
                    filters: appendFilter(
                        false,
                        "Email Address",
                        "mstEmailAddress",
                        listParameters?.filters ?? [],
                        "=isnull=",
                    ),
                })}
                tableColumns={columns}
                searchColumns={EnrolmentSearch}
                getAssignedItems={getAssignedItems}
                handleSubmit={handleSubmit}/>
        </ActivityFrame>
        </>
}

export default Recipients;
